import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';

import { useTranslate } from 'src/locales';
import { SignOutButton } from 'src/layouts/components/sign-out-button';

import { Iconify } from 'src/components/iconify';

import { useAuthContext } from '../hooks';
import { UserProfileFormView } from '../components/user-profile-form-view';

// ----------------------------------------------------------------------

export type UserProfileCreationViewProps = {
  open?: boolean;
  onClose?: () => void;
  mode?: 'create' | 'update';
};

export function UserProfileCreationView({
  open = true,
  onClose,
  mode = 'create',
}: UserProfileCreationViewProps) {
  const { t } = useTranslate('auth');

  const { user, checkUserSession } = useAuthContext();
  const isUpdate = mode === 'update';

  // Update mode edits in place, and a blank field now clears its column, so the
  // form has to open populated rather than empty.
  const currentProfile = isUpdate
    ? {
        fullName: user?.fullName,
        dob: user?.dob,
        photoURL: user?.photoURL,
        office: user?.office,
      }
    : undefined;

  const handleFormSuccess = async () => {
    try {
      // Update user session to fetch the new profile data
      await checkUserSession?.();
      onClose?.();
    } catch (error) {
      console.error('Error updating user session:', error);
      // Still close the dialog even if checkUserSession fails
      onClose?.();
    }
  };

  const handleFormError = () => {
    // Error handling is already done in the form component
    // This is just for any additional error handling if needed
  };

  return (
    <Dialog
      open={open}
      onClose={isUpdate ? onClose : undefined}
      fullScreen={!isUpdate}
      maxWidth={isUpdate ? 'md' : false}
      fullWidth
      disableEscapeKeyDown={!isUpdate}
      sx={{
        '& .MuiDialog-paper': {
          display: 'flex',
          flexDirection: 'column',
          height: isUpdate ? 'auto' : '100%',
          maxHeight: isUpdate ? '90vh' : '100%',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <div>
            <Typography variant={isUpdate ? 'h6' : 'h5'}>
              {isUpdate ? t('profileCreation.editTitle') : t('profileCreation.createTitle')}
            </Typography>
            {!isUpdate && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('profileCreation.subtitle')}
              </Typography>
            )}
          </div>
          {isUpdate ? (
            <IconButton onClick={onClose} edge="end">
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          ) : (
            <SignOutButton variant="text" size="small" sx={{ width: 'max-content' }} />
          )}
        </Stack>
      </DialogTitle>

      <UserProfileFormView
        mode={mode}
        currentProfile={currentProfile}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
        onClose={onClose}
      />
    </Dialog>
  );
}
